import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';

import {
  ArrowLeft,
  CheckCircle,
  Envelope
} from '@phosphor-icons/react';
const AdminForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send reset email');
      }

      setIsSubmitted(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <img 
                src={`${process.env.PUBLIC_URL}/man-and-machine-logo.png`}
                alt="Man & Machine" 
                className="h-16 mx-auto mb-4"
              />
            </Link>
            <p className="text-sm text-[#A0AEC0]">Admin Portal</p>
          </div>

          <div className="glass rounded-3xl p-8 card-glow text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
            <p className="text-[#A0AEC0] mb-6">
              If an account exists with <span className="text-white font-medium">{email}</span>, 
              we&apos;ve sent a password reset link.
            </p>
            <p className="text-sm text-[#718096] mb-6">
              The link will expire in 1 hour. Check your spam folder if you don&apos;t see it.
            </p>

            <Link 
              to="/admin/login"
              className="inline-flex items-center text-[#6366F1] hover:text-[#818CF8] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26] flex items-center justify-center px-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src={`${process.env.PUBLIC_URL}/man-and-machine-logo.png`}
              alt="Man & Machine" 
              className="h-16 mx-auto mb-4"
            />
          </Link>
          <p className="text-sm text-[#A0AEC0]">Admin Portal</p>
        </div>

        <div className="glass rounded-3xl p-8 card-glow">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Forgot Password?</h2>
          <p className="text-[#A0AEC0] text-center mb-6">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Envelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full pl-12 pr-4 py-3 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full relative overflow-hidden rounded-full group disabled:opacity-50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"></div>
              <span className="relative z-10 block px-6 py-3 text-white font-semibold">
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </span>
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/admin/login" 
              className="inline-flex items-center text-sm text-[#6366F1] hover:text-[#818CF8] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminForgotPassword;
